import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import SignalIcon from "./SignalIcon";
import SignalDescription, { Badges } from "./SignalDescription";
import { familyOf, costText, costUnitOf, entityNoun, isAlwaysOn, descriptionParts } from "./signalMeta";

// Signals whose steps don't follow the generic pattern (used when the server sends none).
const OWN_STEPS = {
  hiring_intel: def => [
    "We read what people at your accounts post about the roles they're hiring for.",
    "We pull out the technologies, skills and what they're building.",
    "You see who is hiring, so you can reach them.",
    `${costText(def)}.`
  ]
};

/** The plain steps behind a signal. The server can send its own `steps`; otherwise they follow from what it watches. */
const stepsFor = def => {
  if (Array.isArray(def.steps) && def.steps.length) return def.steps;
  if (OWN_STEPS[def.id]) return OWN_STEPS[def.id](def);
  const free = costUnitOf(def) === "free";
  const many = def.entityType === "ACCOUNT" ? "accounts" : "contacts";
  return [
    free ? "Nothing to pick: it watches the people in your sequences." : `Pick who to watch: all your ${many} or one list.`,
    free ? "Salesgear notices it as it happens." : `On your schedule, Salesgear checks each ${entityNoun(def.entityType)}.`,
    "When something matches, it lands in your live feed.",
    "Act in one click: draft an email, add to a sequence or a list.",
    "Doing the same thing every time? Let an agent do it, with your OK on each draft."
  ];
};

/** "How it works" for one signal: what starts it, the steps, an example, and what it costs. */
const SignalHowItWorks = ({ definition: def, onClose, onTrack }) => {
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const free = costUnitOf(def) === "free";
  const good = [
    ["clock", free ? def.checked || "As it happens" : "Checks monthly by default, weekly if you want moves sooner"],
    ["coin", free ? "Free" : `${costText(def)} · a credit cap pauses it`],
    ["check", "No agent needed: you act from your feed"]
  ];
  // Portalled to the body so the backdrop covers the right rail too (.mainContent is a containing block).
  return ReactDOM.createPortal(
    <div className="sg-modal-bg" onClick={onClose}>
      <div className="sg-modal" role="dialog" aria-modal="true" aria-label={`How ${def.label} works`} onClick={e => e.stopPropagation()}>
        <div className="sg-modal__head">
          <span className={`sg-tile sg-tile--lg ${familyOf(def.category).key}`}><SignalIcon id={def.id} size={22} /></span>
          <div className="sg-grow">
            <h2>{def.label}</h2>
            {!descriptionParts(def) && <p>{def.description}</p>}
            <Badges def={def} />
          </div>
          <button className="sg-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sg-modal__body">
          <SignalDescription def={def} />
          {def.startsOn && def.startsOn !== def.description && <div className="sg-how__starts"><span>Starts on</span>{def.startsOn}</div>}
          <ol className="sg-how__steps">
            {stepsFor(def).map(s => <li key={s}>{s}</li>)}
          </ol>
          {def.example && !descriptionParts(def) && (
            <>
              <h4>What you get</h4>
              <div className="sg-how__example"><SignalIcon id={def.id} size={16} />{def.example}</div>
            </>
          )}
          <h4>Good to know</h4>
          <ul className="sg-how__good">
            {good.map(([icon, text]) => <li key={text}><SignalIcon id={icon} size={14} />{text}</li>)}
          </ul>
        </div>
        <div className="sg-modal__foot">
          <button className="sg-btn sg-btn--ghost" onClick={onClose}>Close</button>
          {def.available !== false && !isAlwaysOn(def) && (
            <button className="sg-btn sg-btn--ghost" onClick={() => onTrack(def)}><SignalIcon id="plus" size={15} /> Track</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SignalHowItWorks;
